import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useState, useEffect, createContext, useContext, useCallback, useRef } from "react";
import { Toaster } from "sonner";
import axios from "axios";

// Pages
import LandingPage from "./pages/LandingPage";
import CarsPage from "./pages/CarsPage";
import VehicleDetailPage from "./pages/VehicleDetailPage";
import ConsultationPage from "./pages/ConsultationPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import AdminDashboard from "./pages/AdminDashboard";
import UserDashboard from "./pages/UserDashboard";
import ProgressPage from "./pages/ProgressPage";

// Hooks
import { usePageTracking } from "./hooks/useAnalytics";

import "@/App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        headers
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
      localStorage.removeItem("token");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    // Trim whitespace that might be added by mobile keyboards
    const cleanEmail = email.trim().toLowerCase();
    const cleanPassword = password.trim();
    
    const response = await axios.post(`${API}/auth/login`, { 
      email: cleanEmail, 
      password: cleanPassword 
    }, { withCredentials: true });
    
    setUser(response.data.user);
    localStorage.setItem("token", response.data.token);
    return response.data;
  };

  const register = async (name, email, password, role = "user") => {
    const response = await axios.post(`${API}/auth/register`, { name, email, password, role });
    setUser(response.data.user);
    localStorage.setItem("token", response.data.token);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
    localStorage.removeItem("token");
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, register, logout, googleLogin, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth Callback Component
const AuthCallback = () => {
  const hasProcessed = useRef(false);
  const { setUser } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionId = hash.split('session_id=')[1]?.split('&')[0];

      if (sessionId) {
        try {
          const response = await axios.post(`${API}/auth/session`, { session_id: sessionId }, { withCredentials: true });
          setUser(response.data);
          
          // Redirect based on role - admin, super_admin and staff go to admin dashboard
          const redirectPath = ['admin', 'super_admin', 'staff'].includes(response.data.role) ? '/admin' : '/dashboard';
          window.location.replace(redirectPath);
        } catch (error) {
          console.error("Auth callback error:", error);
          window.location.replace('/login');
        }
      }
    };

    processAuth();
  }, [location, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F7F2]">
      <div className="text-center">
        <div className="w-12 h-12 spinner mx-auto mb-4"></div>
        <p className="text-[#6B5E55]">Signing you in...</p>
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F7F2]">
        <div className="w-12 h-12 spinner"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Allow admin, super_admin and staff to access admin dashboard
  if (adminOnly && !["admin", "super_admin", "staff"].includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// App Router
const AppRouter = () => {
  const location = useLocation();
  
  // Track page views
  usePageTracking();

  // Check URL fragment for session_id (OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/cars" element={<CarsPage />} />
      <Route path="/cars/:vehicleId" element={<VehicleDetailPage />} />
      <Route path="/consultation" element={<ConsultationPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/progress/:leadId" element={<ProgressPage />} />
      <Route
        path="/admin/*"
        element={
          <ProtectedRoute adminOnly>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/*"
        element={
          <ProtectedRoute>
            <UserDashboard />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
export { API, BACKEND_URL };
